// Payments reconciliation queue — tenant-wide payments the provider never gave
// a definitive answer for (parked as reconciliation_required after the staleness
// window). Each one must be resolved by a human against the provider portal or
// bank statement; the decision + note land on the immutable audit trail.
//
// The queue now lives on the Finance hub (payroll/finance?tab=reconciliation);
// the old payroll/reconciliation route redirects there so links keep working.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CheckCircle2, Copy, Smartphone, Landmark } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Skeleton } from '@/features/shared/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/features/shared/components/ui/table';
import { payrollApi } from '../api/payrollApi';
import { formatMoney, formatDate } from '../lib/format';
import { ResolveDisbursementDialog } from '../components/ResolveDisbursementDialog';
import type { DisbursementItem } from '../types';

/** Whole days since an ISO timestamp (0 for today). */
function ageInDays(iso: string): number {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 0;
  return Math.max(0, Math.floor((Date.now() - then) / 86_400_000));
}

/** The reconciliation queue body (table + resolve dialog) — rendered by the
 *  Finance hub's Reconciliation tab. `onCountChange` lets the hub keep its
 *  badge in sync as items load / get resolved. */
export const ReconciliationQueue: React.FC<{ onCountChange?: (count: number) => void }> = ({
  onCountChange,
}) => {
  const { t } = useTranslation('payroll');
  const [items, setItemsRaw] = useState<DisbursementItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [resolveItem, setResolveItem] = useState<DisbursementItem | null>(null);

  const setItems = (updater: React.SetStateAction<DisbursementItem[]>) => {
    setItemsRaw((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      onCountChange?.(next.length);
      return next;
    });
  };

  useEffect(() => {
    payrollApi
      .getReconciliationQueue()
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function copyRef(ref: string) {
    try {
      await navigator.clipboard.writeText(ref);
      toast.success(t('reconciliation.refCopied', { defaultValue: 'Reference copied' }));
    } catch {
      toast.error(t('reconciliation.refCopyFailed', { defaultValue: 'Could not copy the reference' }));
    }
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        {t('reconciliation.description', {
          defaultValue:
            'Payments with no provider answer after the staleness window — resolve each against the provider portal or statement.',
        })}
      </p>

      <Card>
        <CardContent className="p-5">
          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
              <CheckCircle2 className="h-8 w-8 text-[#166534]" />
              <p className="font-medium text-[#166534]">
                {t('reconciliation.emptyTitle', { defaultValue: 'Nothing needs reconciliation' })}
              </p>
              <p className="text-sm text-muted-foreground">
                {t('reconciliation.emptyBody', {
                  defaultValue: 'Every submitted payment has a definitive provider answer.',
                })}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('reconciliation.colBeneficiary', { defaultValue: 'Beneficiary' })}</TableHead>
                  <TableHead>{t('reconciliation.colDestination', { defaultValue: 'Destination' })}</TableHead>
                  <TableHead className="text-right">{t('reconciliation.colAmount', { defaultValue: 'Amount' })}</TableHead>
                  <TableHead>{t('reconciliation.colReference', { defaultValue: 'Provider / reference' })}</TableHead>
                  <TableHead>{t('reconciliation.colSubmitted', { defaultValue: 'Submitted' })}</TableHead>
                  <TableHead>{t('reconciliation.colNote', { defaultValue: 'Note' })}</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((it) => (
                  <TableRow key={it.id}>
                    <TableCell className="font-medium text-charcoal">{it.employeeName}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1.5 font-mono text-xs text-muted-foreground">
                        {it.channel === 'mobile_money' ? (
                          <Smartphone className="h-4 w-4" />
                        ) : (
                          <Landmark className="h-4 w-4" />
                        )}
                        {it.accountMasked}
                      </span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-charcoal">
                      {formatMoney(it.amount, it.currency ?? 'TZS')}
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                        {it.provider}
                        {it.connectorRef && (
                          <>
                            <span className="font-mono">{it.connectorRef}</span>
                            <button
                              type="button"
                              className="text-muted-foreground hover:text-charcoal"
                              title={t('reconciliation.copyRef', { defaultValue: 'Copy reference' })}
                              onClick={() => copyRef(it.connectorRef!)}
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </button>
                          </>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {it.submittedAt ? (
                        <>
                          {formatDate(it.submittedAt)}
                          <span className="block text-xs">
                            {t('reconciliation.ageDays', {
                              defaultValue: '{{count}} day(s) ago',
                              count: ageInDays(it.submittedAt),
                            })}
                          </span>
                        </>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="max-w-[16rem] text-xs text-muted-foreground">
                      {it.reconciliationNote || '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => setResolveItem(it)}>
                        {t('disbursement.resolve', { defaultValue: 'Resolve' })}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ResolveDisbursementDialog
        item={resolveItem}
        onClose={() => setResolveItem(null)}
        // A resolved payment leaves the queue — drop it from the table.
        onResolved={(updated) => setItems((prev) => prev.filter((x) => x.id !== updated.id))}
      />
    </div>
  );
};

/** Legacy route (payroll/reconciliation) — redirect to the Finance hub tab so
 *  there is exactly one canonical reconciliation surface. */
export const ReconciliationPage: React.FC = () => {
  const { pathname } = useLocation();
  return (
    <Navigate
      to={{ pathname: pathname.replace(/reconciliation\/?$/, 'finance'), search: '?tab=reconciliation' }}
      replace
    />
  );
};

export default ReconciliationPage;
